import { useMemo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { BarChart3, CalendarCheck, ListTodo, MessageCircle, Settings } from 'lucide-react';
import { I18nProvider, useI18n } from './i18n';
import { AppStateProvider, useAppData, usePrefs } from './state';
import { ToastProvider } from './ui/Toast';
import { FloatingNavigation } from './ui/FloatingNavigation';
import { ErrorBoundary } from './ErrorBoundary';
import { TodayScreen } from './screens/TodayScreen';
import { HabitsScreen } from './screens/HabitsScreen';
import { HabitEditor } from './screens/HabitEditor';
import { HabitDetail } from './screens/HabitDetail';
import { BrainScreen } from './screens/BrainScreen';
import { ChatScreen } from './screens/ChatScreen';
import { ShareTarget } from './screens/ShareTarget';
import { AssistantSettingsScreen } from './screens/AssistantSettingsScreen';
import { FocusScreen } from './screens/FocusScreen';
import { ReviewScreen } from './screens/ReviewScreen';
import { StatsScreen } from './screens/StatsScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { AccountScreen } from './screens/AccountScreen';
import { NotificationsScreen } from './screens/NotificationsScreen';
import { PlanScreen } from './screens/PlanScreen';
import { Onboarding } from './screens/Onboarding';
import { UpdatePrompt } from './UpdatePrompt';
import { SyncRunner } from './sync/SyncRunner';
import { useAchievementToast } from './domain/achievements';

function Nav() {
  const { t, m } = useI18n();
  const items = [
    { to: '/', label: t('nav.today'), Icon: CalendarCheck, end: true },
    { to: '/habits', label: t('nav.habits'), Icon: ListTodo },
    { to: '/chat', label: t('nav.chat'), Icon: MessageCircle, featured: true },
    { to: '/stats', label: t('nav.stats'), Icon: BarChart3 },
    { to: '/settings', label: t('nav.settings'), Icon: Settings },
  ];
  return <FloatingNavigation items={items} label={t('nav.main')} brand={m.app.name} />;
}

function Shell() {
  const { t } = useI18n();
  useAchievementToast();
  return (
    <div className="app">
      <a href="#main" className="skip-link">
        {t('nav.main')}
      </a>
      <Nav />
      <main className="main" id="main" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<TodayScreen />} />
          <Route path="/habits" element={<HabitsScreen />} />
          <Route path="/habits/new" element={<HabitEditor />} />
          <Route path="/habits/:id" element={<HabitDetail />} />
          <Route path="/habits/:id/edit" element={<HabitEditor />} />
          <Route path="/stats" element={<StatsScreen />} />
          <Route path="/chat" element={<ChatScreen />} />
          <Route path="/share" element={<ShareTarget />} />
          <Route path="/brain" element={<BrainScreen />} />
          <Route path="/focus" element={<FocusScreen />} />
          <Route path="/review" element={<ReviewScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/settings/account" element={<AccountScreen />} />
          <Route path="/settings/notifications" element={<NotificationsScreen />} />
          <Route path="/settings/plan" element={<PlanScreen />} />
          <Route path="/settings/assistant" element={<AssistantSettingsScreen />} />
          <Route path="*" element={<TodayScreen />} />
        </Routes>
      </main>
    </div>
  );
}

function ErrorScreen({ storage }: { storage?: boolean }) {
  const { t } = useI18n();
  return (
    <div className="error-screen" role="alert">
      <div className="stack" style={{ maxWidth: 420, alignItems: 'center' }}>
        <h1>{t('errors.title')}</h1>
        <p className="muted">{storage ? t('errors.storage') : t('errors.body')}</p>
        <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
          {t('errors.reload')}
        </button>
      </div>
    </div>
  );
}

function Loaded() {
  const data = useAppData();
  const prefs = usePrefs();
  const prefsValue = useMemo(
    () => (prefs.locale ? { locale: prefs.locale, theme: prefs.theme, setLocale: prefs.setLocale, setTheme: prefs.setTheme } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefs.locale, prefs.theme],
  );
  if (!data || !prefsValue) return <div className="error-screen" aria-busy="true" />;
  return (
    <I18nProvider locale={prefsValue.locale}>
      <ToastProvider>
        <ErrorBoundary fallback={() => <ErrorScreen />}>
          {data.settings ? (
            <AppStateProvider data={data} prefs={prefsValue}>
              <BrowserRouter>
                <Shell />
                <SyncRunner />
              </BrowserRouter>
            </AppStateProvider>
          ) : (
            <Onboarding />
          )}
          <UpdatePrompt />
        </ErrorBoundary>
      </ToastProvider>
    </I18nProvider>
  );
}

export function App({ storageError }: { storageError?: boolean }) {
  if (storageError) {
    return (
      <I18nProvider locale="es">
        <ErrorScreen storage />
      </I18nProvider>
    );
  }
  return <Loaded />;
}
